import { useQueryClient, useMutation, useQuery } from 'react-query';
import { useBrandConfig, useBrandId, useConfig } from '@ux/whitelabel';
import cookies from 'browser-cookies';
import { dispatchGAEvent } from '@uds/tagmg';
import { useMemo, createContext, useContext } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';
import { pipe, json, bodyOnly, fetch } from '@ux/drivers';

const useDefaultLanguage = () => {
  var _useBrandConfig, _useBrandConfig$local;
  return (_useBrandConfig = useBrandConfig()) === null || _useBrandConfig === void 0 ? void 0 : (_useBrandConfig$local = _useBrandConfig.locale) === null || _useBrandConfig$local === void 0 ? void 0 : _useBrandConfig$local.default;
};
const useSupportedLanguages = () => {
  var _useBrandConfig$langu, _useBrandConfig2;
  return (_useBrandConfig$langu = (_useBrandConfig2 = useBrandConfig()) === null || _useBrandConfig2 === void 0 ? void 0 : _useBrandConfig2.languages) != null ? _useBrandConfig$langu : ['en-gb'];
};
const useDomain = () => {
  var _useBrandConfig3;
  const domain = (_useBrandConfig3 = useBrandConfig()) === null || _useBrandConfig3 === void 0 ? void 0 : _useBrandConfig3.domain;
  try {
    return COOKIE_DOMAIN;
  } catch (e) {
    return domain;
  }
};

/* eslint-disable no-param-reassign */
const findClosestLanguage = (browser, supported) => {
  let closest = browser;
  if (closest.startsWith('de')) {
    closest = 'de-de';
  }
  if (closest.startsWith('en')) {
    closest = 'en-gb';
  }
  const isValid = supported.includes(closest);
  if (isValid) {
    return closest;
  }
  return supported[0];
};
const determinePreferredLanguage = (preferred, browser, supported) => {
  browser = browser.toLowerCase();
  supported = supported.map(l => l.toLowerCase());
  if (preferred != null) {
    preferred = preferred.toLowerCase();
    const isValid = supported.includes(preferred);
    if (isValid) {
      return preferred;
    }
  }
  const isValid = supported.includes(browser);
  if (isValid) {
    return browser;
  }
  return findClosestLanguage(browser, supported);
};
var determinePreferredLanguage$1 = determinePreferredLanguage;

var driver = (() => pipe(json(), bodyOnly())(fetch));

const fetchMessages = _ref => {
  let {
    lexiconUrl,
    window,
    dispatchGAEvent,
    initialised = false
  } = _ref;
  return async (brandId, language, defaultLanguage, scopes) => {
    if (!initialised) {
      var _window$hugBootstrap;
      // eslint-disable-next-line no-param-reassign
      initialised = true;
      const messages = (_window$hugBootstrap = window.hugBootstrap) === null || _window$hugBootstrap === void 0 ? void 0 : _window$hugBootstrap.lexicon;
      if (messages != null) {
        return messages;
      }
    }
    const url = `${lexiconUrl}/${brandId}`;
    const config = {
      url,
      data: {
        locales: [language, defaultLanguage],
        scopes,
        flat: true
      }
    };
    const requestRttStart = window.performance.now();
    try {
      const result = await driver()(config);
      // report how long lexicon call has taken
      const now = window.performance.now();
      const rttDiff = now - requestRttStart;
      dispatchGAEvent({
        hitType: 'timing',
        timingCategory: 'API RTT',
        timingLabel: 'Lexicon',
        timingVar: 'load',
        timingValue: Math.round(rttDiff)
      });
      return result;
    } catch (error) {
      // bpt should still return the right translations, lets try a hard reload
      // instead of showing a white screen;
      // but let's avoid infinite retry
      const shouldRetry = () => {
        const key = 'fetch-message-retry';
        const lastRetry = window.sessionStorage.getItem(key);
        if (lastRetry == null) {
          window.sessionStorage.setItem(key, new Date().toISOString());
          return true;
        }
        const fiveMinutes = 1000 * 60 * 5;
        return new Date(lastRetry).getTime() - Date.now() >= fiveMinutes;
      };
      if (shouldRetry()) {
        window.location.reload();
      }
    }
  };
};
var fetchMessages$1 = fetchMessages;

const getBrowserLanguage = window => () => {
  const navigator = window.navigator;
  if (Array.isArray(navigator.languages)) {
    return navigator.languages[0];
  }
  if (navigator.language) {
    return navigator.language;
  }
  if (navigator.browserLanguage != null) {
    return navigator.browserLanguage;
  }
  return 'en';
};
var getBrowserLanguage$1 = getBrowserLanguage;

function getLexiconUrl() {
  try {
    return LEXICON_API_URL;
  } catch (e) {
    return 'https://green.lexicon.api.heg-cp.com/v2/vocab';
  }
}

const getPreferredLanguage = cookies => () => {
  var _cookies$get;
  return (_cookies$get = cookies.get('ux-language-preferred')) != null ? _cookies$get : '';
};
const setPreferredLanguage = cookies => (v, domain) => {
  cookies.set('ux-language-preferred', v, {
    domain
  });
};

const language = '@ux/language/language';

function useRefetchLanguage() {
  const queryCache = useQueryClient();
  return () => {
    queryCache.invalidateQueries(language);
  };
}

const useSaveLanguage = function () {
  let getBrowser = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getBrowserLanguage$1(window);
  let getCookie = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : getPreferredLanguage(cookies);
  let useGetSupported = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : useSupportedLanguages;
  let useGetDomain = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : useDomain;
  let save = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : setPreferredLanguage(cookies);
  let useGetRefetchLanguage = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : useRefetchLanguage;
  let opts = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
  const supported = useGetSupported();
  const domain = useGetDomain();
  const refetch = useGetRefetchLanguage();
  const fn = vars => {
    const cookie = getCookie();
    const browser = getBrowser();
    const value = determinePreferredLanguage$1(vars.language, browser, supported);
    const changed = cookie !== value;
    if (changed) {
      save(value, domain);
      refetch();
    }
    return Promise.resolve();
  };
  return useMutation(fn, opts);
};
function useUseSaveLanguage() {
  return useSaveLanguage();
}

const LanguageContext = /*#__PURE__*/createContext(undefined);
const LanguageProvider = _ref => {
  let {
    children,
    scopes
  } = _ref;
  const state = useMemo(() => ({
    scopes
  }), [scopes]);
  return /*#__PURE__*/jsx(LanguageContext.Provider, {
    value: state,
    children: children
  });
};
const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (context == null) {
    throw new Error('Expected context to be defined, but it was not, did you forget the LanguageProvider?');
  }
  return context;
};
const useScopes = () => {
  const context = useLanguageContext();
  return context.scopes;
};

const getLatestLanguage = async (getBrowser, getCookie, save) => {
  let cookie = getCookie();
  if (!cookie) {
    const browser = getBrowser();
    await save({
      language: browser
    });
    cookie = getCookie();
  }
  return Promise.resolve(cookie);
};
const getLatestMessages = (latestLanguage, defaultLanguage, brandId, scopes, fetch) => {
  return fetch(brandId, latestLanguage, defaultLanguage, scopes);
};
const getEnabled = (brandId, defaultLanguage, scopes, whitelabelStatus) => {
  return Boolean(brandId && defaultLanguage && (scopes === null || scopes === void 0 ? void 0 : scopes.length) && whitelabelStatus === 'success');
};
const useUxLanguage = (opts, getBrowser, getCookie, save, defaultLanguage, brandId, scopes, whitelabelStatus, fetchMessages) => {
  const enabled = getEnabled(brandId, defaultLanguage, scopes, whitelabelStatus);
  const callback = async () => {
    const language = await getLatestLanguage(getBrowser, getCookie, save);
    const messages = await getLatestMessages(language, defaultLanguage, brandId, scopes, fetchMessages);
    return {
      language,
      messages
    };
  };
  return useQuery(language, callback, {
    enabled,
    ...opts
  });
};
function useUseUxLanguage(opts) {
  const getBrowser = getBrowserLanguage$1(window);
  const getCookie = getPreferredLanguage(cookies);
  const {
    mutate: save
  } = useSaveLanguage();
  const defaultLanguage = useDefaultLanguage();
  const brandId = useBrandId();
  const scopes = useScopes();
  const {
    status: whitelabelStatus
  } = useConfig();
  const fetchMessagesFn = fetchMessages$1({
    lexiconUrl: getLexiconUrl(),
    window,
    dispatchGAEvent
  });
  return useUxLanguage(opts, getBrowser, getCookie, save, defaultLanguage, brandId, scopes, whitelabelStatus, fetchMessagesFn);
}

function useLanguage(opts) {
  return useUseUxLanguage({
    ...opts,
    select: data => data.language
  });
}

function useUseInitialize(opts) {
  const {
    status
  } = useLanguage(opts);
  const ready = status === 'success';
  return [ready];
}

function useMessages(opts) {
  return useUseUxLanguage({
    ...opts,
    select: data => data.messages
  });
}

/*
 * Functions to handle returning countries and locales from LCID strings
 */

const splitCode = code => {
  // e.g. fr-ca => ca
  if (code.indexOf('-') > -1) {
    return code.split('-');
  }

  // e.g. fr.ca => ca
  if (code.indexOf('.') > -1) {
    return code.split('.');
  }
  return [code, code];
};
const getCountryCode = code => splitCode(code)[1];
const getLocaleCode = code => splitCode(code)[0];

const TestProvider = _ref => {
  let {
    children,
    language = '',
    messages = {}
  } = _ref;
  useUseUxLanguage({
    // From the docs:
    // If you configure your query observer with initialData,
    // and no staleTime (the default staleTime: 0), the query will immediately
    // refetch when it mounts.
    // Meaning that we must set staleTime as well, so it doesn't try to fetch
    // Again during our test.
    initialData: {
      language,
      messages
    },
    staleTime: 1000 * 60 * 5
  });
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
};
const Provider = _ref2 => {
  let {
    language = 'en-gb',
    messages = {},
    children,
    scopes = ['ux']
  } = _ref2;
  const state = useMemo(() => ({
    scopes
  }), [scopes]);
  return /*#__PURE__*/jsx(LanguageContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx(TestProvider, {
      language: language,
      messages: messages,
      children: children
    })
  });
};
var Provider$1 = Provider;

export { LanguageProvider, Provider$1 as Provider, getCountryCode, getLocaleCode, useDefaultLanguage, useUseInitialize as useInitialize, useLanguage, useMessages, useRefetchLanguage, useRefetchLanguage as useRefetchMessages, useUseSaveLanguage as useSaveLanguage, useSupportedLanguages };
